import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import snakeCase from 'lodash/snakeCase';
import RelatedProductsCollection
  from '@fuww/library/src/RelatedProducts';
import { RelatedProductCard }
  from '@fuww/library/src/RelatedProducts/RelatedProductCard';
import { PortraitImageContainer } from '@fuww/library/src/Card';
import { useSiteContext } from '../SiteContext';
import Image from '../Image';
import Routes from '../../routes.mjs';
import withQuery from '../../lib/withQuery';
import { getTargetOrigin } from '../../lib/redirectRouteToDomain';

const relatedProductsPropertyTypes = {
  data: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      brand: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      id: PropTypes.number.isRequired,
      imageUrls: PropTypes.arrayOf(PropTypes.string.isRequired),
      name: PropTypes.string.isRequired,
      sourceName: PropTypes.string.isRequired,
      url: PropTypes.string,
    }).isRequired).isRequired,
  }).isRequired,
  callToActionMessage: PropTypes.string,
  headerText: PropTypes.string.isRequired,
  isFashionFinder: PropTypes.bool,
};

type RelatedProductsProperties = InferProps<
  typeof relatedProductsPropertyTypes
>;

const RelatedProducts = ({
  data,
  callToActionMessage,
  headerText,
  isFashionFinder,
}: RelatedProductsProperties) => {
  const { origin } = useSiteContext();
  const { products } = data;

  return (
    <RelatedProductsCollection
      title={headerText}
      products={products.map((product) => {
        const {
          brand, id, imageUrls, name, sourceName, url,
        } = product;

        const productUrl = url && isFashionFinder
          ? url
          : `${getTargetOrigin(origin)}${Routes.findAndGetUrls(
            'product', { id: id.toString() }).urls.as
          }`;

        return (
          <RelatedProductCard
            key={id}
            graphic={
              imageUrls
                && (
                  <PortraitImageContainer>
                    <Image
                      placeholder={imageUrls[0]}
                      src={imageUrls[1]}
                      srcSet={`${imageUrls[1]
                      } 138w, ${imageUrls[2]
                      } 276w, ${imageUrls[3]
                      } 552w`}
                      alt={name}
                      width="138px"
                      height="184px"
                      sizes="138px"
                    />
                  </PortraitImageContainer>
                )
            }
            name={brand.name}
            url={productUrl}
            callToActionMessage={callToActionMessage}
            analyticsClassNames={
              [
                `brand-${snakeCase(brand.name)}`,
                `retailer-${snakeCase(sourceName)}`,
              ]
            }
          />
        );
      })}
    />
  );
};

RelatedProducts.propTypes = relatedProductsPropertyTypes;

export default withQuery(RelatedProducts);
